import React from "react";
import {
  Autocomplete,
  MenuItem,
  TextField as MuiTextField,
} from "@mui/material";
import TextField from "../TextField";

interface ProvincePickerProps {
  formikVal: string;
  formikErrors: boolean;
  formikStringError?: string;
  formikSetFieldValue: (field: string, value: any) => void;
}

export const provinceOptions = [
  { value: "AB", label: "Alberta" },
  { value: "BC", label: "British Columbia" },
  { value: "MB", label: "Manitoba" },
  { value: "NB", label: "New Brunswick" },
  { value: "NL", label: "Newfoundland and Labrador" },
  { value: "NT", label: "Northwest Territories" },
  { value: "NS", label: "Nova Scotia" },
  { value: "NU", label: "Nunavut" },
  { value: "ON", label: "Ontario" },
  { value: "PE", label: "Prince Edward Island" },
  { value: "QC", label: "Quebec" },
  { value: "SK", label: "Saskatchewan" },
  { value: "YT", label: "Yukon" },
  // US States
  { value: "AL", label: "Alabama" },
  { value: "AK", label: "Alaska" },
  { value: "AZ", label: "Arizona" },
  { value: "AR", label: "Arkansas" },
  { value: "CA", label: "California" },
  { value: "CZ", label: "Canal Zone" },
  { value: "CO", label: "Colorado" },
  { value: "CT", label: "Connecticut" },
  { value: "DE", label: "Delaware" },
  { value: "DC", label: "District of Columbia" },
  { value: "FL", label: "Florida" },
  { value: "GA", label: "Georgia" },
  { value: "GU", label: "Guam" },
  { value: "HI", label: "Hawaii" },
  { value: "ID", label: "Idaho" },
  { value: "IL", label: "Illinois" },
  { value: "IN", label: "Indiana" },
  { value: "IA", label: "Iowa" },
  { value: "KS", label: "Kansas" },
  { value: "KY", label: "Kentucky" },
  { value: "LA", label: "Louisiana" },
  { value: "ME", label: "Maine" },
  { value: "MD", label: "Maryland" },
  { value: "MA", label: "Massachusetts" },
  { value: "MI", label: "Michigan" },
  { value: "MN", label: "Minnesota" },
  { value: "MS", label: "Mississippi" },
  { value: "MO", label: "Missouri" },
  { value: "MT", label: "Montana" },
  { value: "NE", label: "Nebraska" },
  { value: "NV", label: "Nevada" },
  { value: "NH", label: "New Hampshire" },
  { value: "NJ", label: "New Jersey" },
  { value: "NM", label: "New Mexico" },
  { value: "NY", label: "New York" },
  { value: "NC", label: "North Carolina" },
  { value: "ND", label: "North Dakota" },
  { value: "OH", label: "Ohio" },
  { value: "OK", label: "Oklahoma" },
  { value: "OR", label: "Oregon" },
  { value: "PA", label: "Pennsylvania" },
  { value: "PR", label: "Puerto Rico" },
  { value: "RI", label: "Rhode Island" },
  { value: "SC", label: "South Carolina" },
  { value: "SD", label: "South Dakota" },
  { value: "TN", label: "Tennessee" },
  { value: "TX", label: "Texas" },
  { value: "UT", label: "Utah" },
  { value: "VT", label: "Vermont" },
  { value: "VI", label: "Virgin Islands" },
  { value: "VA", label: "Virginia" },
  { value: "WA", label: "Washington" },
  { value: "WV", label: "West Virginia" },
  { value: "WI", label: "Wisconsin" },
  { value: "WY", label: "Wyoming" },
];

const canadianProvinceCodes = new Set(
  provinceOptions.slice(0, 13).map((province) => province.value),
);

type ProvinceAutocompleteProps = {
  value: string;
  onChange: (value: string) => void;
  onBlur: () => void;
  error?: string;
};

export const ProvinceAutocomplete = ({
  value,
  onChange,
  onBlur,
  error,
}: ProvinceAutocompleteProps): React.ReactElement => (
  <div className="mb-5 min-w-0">
    <label
      htmlFor="address.province"
      className="mb-2 block text-base font-semibold leading-5 text-black"
    >
      Province/State
    </label>
    <Autocomplete
      id="address.province"
      value={
        provinceOptions.find((province) => province.value === value) || null
      }
      onChange={(_, selectedProvince) =>
        onChange(selectedProvince?.value || "")
      }
      onBlur={onBlur}
      options={provinceOptions}
      groupBy={(province) =>
        canadianProvinceCodes.has(province.value)
          ? "Canada"
          : "United States"
      }
      getOptionLabel={(province) => province.label}
      isOptionEqualToValue={(option, selectedProvince) =>
        option.value === selectedProvince.value
      }
      renderInput={(params) => (
        <MuiTextField
          {...params}
          error={Boolean(error)}
          helperText={error}
          placeholder="Select Province/State"
          sx={{
            "& .MuiOutlinedInput-root": {
              backgroundColor: "white",
              borderRadius: "15px",
              "& fieldset": {
                borderColor: "rgba(0, 0, 0, 0.87)",
                borderRadius: "15px",
              },
              "&:hover fieldset": {
                borderColor: "rgba(0, 0, 0, 0.87)",
              },
              "&.Mui-focused fieldset": {
                borderWidth: 1,
                borderColor: "#E1A731",
              },
            },
            "& .MuiInputBase-input": {
              color: "#333333",
              fontSize: 16,
            },
            "& .MuiFormHelperText-root": {
              marginLeft: 0,
            },
          }}
        />
      )}
    />
  </div>
);

const ProvincePicker: React.FC<ProvincePickerProps> = ({
  formikVal,
  formikErrors,
  formikStringError,
  formikSetFieldValue,
}) => {
  return (
    <div className="mb-6">
      <div className="flex items-center mb-1">
        <label
          htmlFor="province"
          className="block text-sm font-medium text-gray-900"
        >
          Province/State
          <span className="text-red-500 ml-1">*</span>
        </label>
      </div>
      <TextField
        select
        id="province"
        value={formikVal || ""}
        onChange={(e) =>
          formikSetFieldValue("address.province", e.target.value)
        }
        error={formikErrors}
        helperText={formikStringError}
        fullWidth
      >
        {provinceOptions.map((province) => (
          <MenuItem key={province.value} value={province.value}>
            {province.label}
          </MenuItem>
        ))}
      </TextField>
    </div>
  );
};

export default ProvincePicker;
